import { Loader2, Plus, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { addToWatchlist, searchStocks } from '../api';
import { SearchResult } from '../types';

interface Props {
  onAdded: () => void;
}

// 🎨 규격 V2(08-23) 부품 — 뜻 없는 라벨은 중립 칩 하나로(밝은 원색·보라 은퇴).
const MINICHIP = 'inline-flex items-center h-[18px] px-1.5 rounded-[5px] border text-[10.5px] font-medium whitespace-nowrap';
const TONE_MUTE = 'border-dc-line1 bg-dc-bg1 text-dc-ink2';

const MARKET_STYLE: Record<string, string> = {
  KOSPI: TONE_MUTE,
  KOSDAQ: TONE_MUTE,
};

export default function SearchBar({ onAdded }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // debounce 검색
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await searchStocks(query);
        setResults(data);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, [query]);

  const handleAdd = async (item: SearchResult) => {
    setAdding(item.ticker);
    try {
      await addToWatchlist(item.ticker);
      setQuery('');
      setResults([]);
      setOpen(false);
      onAdded();
    } catch {
      // 이미 있거나 실패
    } finally {
      setAdding(null);
    }
  };

  return (
    <div ref={wrapperRef} className="relative w-full max-w-md">
      <div className="flex items-center gap-2 px-3 py-2 bg-dc-bg1 border border-[#202A3C] rounded-lg focus-within:border-dc-point/50 transition-colors">
        {searching ? (
          <Loader2 size={14} className="text-dc-ink3 animate-spin shrink-0" />
        ) : (
          <Search size={14} className="text-dc-ink3 shrink-0" />
        )}
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="종목명 또는 티커 입력  예: 삼성전자, 005930"
          className="flex-1 bg-transparent text-dc-ink2 text-xs placeholder-dc-ink3/70 outline-none"
        />
      </div>

      {open && results.length > 0 && (
        <div className="absolute top-full mt-1 left-0 right-0 bg-dc-bg1 border border-dc-line1 rounded-lg z-50 overflow-hidden">
          {results.map((item) => (
            <button
              key={item.ticker}
              onClick={() => handleAdd(item)}
              disabled={adding === item.ticker}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.015] transition-colors text-left group"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-dc-ink1 text-[13px] font-semibold truncate group-hover:text-dc-point transition-colors">{item.name}</span>
                  <span className="text-dc-ink3 text-[11.5px] shrink-0">{item.ticker}</span>
                </div>
              </div>
              <span className={`${MINICHIP} shrink-0 ${MARKET_STYLE[item.market] ?? TONE_MUTE}`}>
                {item.market}
              </span>
              <span className="text-dc-ink3 group-hover:text-dc-point transition-colors shrink-0">
                {adding === item.ticker
                  ? <Loader2 size={13} className="animate-spin" />
                  : <Plus size={13} />
                }
              </span>
            </button>
          ))}
        </div>
      )}

      {open && query.trim() && !searching && results.length === 0 && (
        <div className="absolute top-full mt-1 left-0 right-0 bg-dc-bg1 border border-dc-line1 rounded-lg z-50 px-4 py-3 text-dc-ink3 text-[13px]">
          검색 결과 없음
        </div>
      )}
    </div>
  );
}
